import axios from 'axios';
import { call, delay, put, race, select, take, takeEvery, takeLeading } from 'redux-saga/effects';

import { categoriesActions } from 'app-components/flux/categories';
import { resourceSagaUtils, resourceStoreTypes } from 'app-components/flux/core';
import { documentsActions, documentsTypes } from 'app-components/flux/documents';
import { tagsActions } from 'app-components/flux/tags';
import { transactionsActions } from 'app-components/flux/transactions';
import createSharedCallback from 'hooks/createSharedCallback';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { tracker } from 'app-components/utils/core';
import {
  importData,
  importDataFailure,
  importDataSuccess,
  pfmImport,
  pfmImportFailure,
  pfmImportHistoricalBalancesImport,
  pfmImportHistoricalBalancesImportFailure,
  pfmImportHistoricalBalancesImportSuccess,
  pfmImportMintPromoExtend,
  pfmImportMintPromoExtendFailure,
  pfmImportMintPromoExtendSuccess,
  pfmImportMultiAccountImport,
  pfmImportMultiAccountImportFailure,
  pfmImportMultiAccountImportSuccess,
  pfmImportParsePost,
  pfmImportParsePostFailure,
  pfmImportParsePostSuccess,
  pfmImportSuccess,
} from 'data/import/importActions';
import type {
  BumpResponseType,
  HistoricalBalancePayloadType,
  ImportDataPayloadType,
  PfmImportPayloadType,
} from './importTypes';

const [registerCallback, unregisterCallback, executeCallback] = createSharedCallback<BumpResponseType>();
export { registerCallback, unregisterCallback };

const historicalBalancesConfig =
  resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<HistoricalBalancePayloadType>()({
    resourceName: 'pfm-importer-historical-balances',
    resourceBaseUrl: '/pfm-importer/import-balances',
    successAction: pfmImportHistoricalBalancesImportSuccess,
    failureAction: pfmImportHistoricalBalancesImportFailure,
  });

export function* pfmImportHistoricalBalancesActionWatcher() {
  yield takeEvery(
    pfmImportHistoricalBalancesImport.type,
    resourceSagaUtils.qcsSyncCreateResource<HistoricalBalancePayloadType>,
    historicalBalancesConfig,
  );
}

const parsePostConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<any>()({
  resourceName: 'pfm-importer-parse-post',
  resourceBaseUrl: (action) => `/pfm-importer/parse/${action.payload}`,
  successAction: pfmImportParsePostSuccess,
  failureAction: pfmImportParsePostFailure,
});

export function* pfmImportParsePostActionWatcher() {
  yield takeEvery(pfmImportParsePost.type, resourceSagaUtils.qcsSyncCreateResource<any>, parsePostConfig);
}

const parseMultiAccountPostConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<any>()({
  resourceName: 'pfm-importer-multi-import-post',
  resourceBaseUrl: '/pfm-importer/multi-account-import',
  successAction: pfmImportMultiAccountImportSuccess,
  failureAction: pfmImportMultiAccountImportFailure,
});

export function* pfmImportMultiAccountImportActionWatcher() {
  yield takeEvery(
    pfmImportMultiAccountImport.type,
    resourceSagaUtils.qcsSyncCreateResource<any>,
    parseMultiAccountPostConfig,
  );
}

export function* pfmImportActionWatcher() {
  const config = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<PfmImportPayloadType>()({
    resourceName: 'pfm-importer',
    resourceBaseUrl: '/pfm-importer/import?retry=true',
    getLastSyncDate: () => null,
    successAction: pfmImportSuccess,
    failureAction: pfmImportFailure,
    transformResourceWithResponse: (resource, response) => ({ ...resource, ...response }),
  });
  yield takeEvery(pfmImport.type, resourceSagaUtils.qcsSyncCreateResource<PfmImportPayloadType>, config);
}

export function* importDataActionWatcher() {
  yield takeEvery(
    importData,
    function* _importData(importDataAction: { meta: any; payload: ImportDataPayloadType; type: 'IMPORT_DATA' }) {
      tracker.track(tracker.events.csvImport, { status: 'start', type: importDataAction.payload.provider });

      yield put(documentsActions.createDocument(documentsTypes.mkDocument(importDataAction.payload.document)));
      const { createDocumentSuccessAction, createDocumentFailureAction } = yield race({
        createDocumentSuccessAction: take(
          // @ts-expect-error FIXME TS7006
          (action) =>
            action.type === documentsActions.createDocumentSuccess.toString() &&
            action.payload.clientId &&
            action.payload.clientId === importDataAction.payload.document.clientId,
        ),
        createDocumentFailureAction: take(
          // @ts-expect-error FIXME TS7006
          (action) =>
            action.type === documentsActions.createDocumentFailure.toString() &&
            action.payload.clientId &&
            action.payload.clientId === importDataAction.payload.document.clientId,
        ),
      });

      if (
        createDocumentSuccessAction &&
        createDocumentSuccessAction.payload.url &&
        createDocumentSuccessAction.payload.contentType &&
        createDocumentSuccessAction.payload.id
      ) {
        let dataUploadResponse;
        try {
          // @ts-expect-error FIXME TS7057
          dataUploadResponse = yield call(
            axios.put,
            createDocumentSuccessAction.payload.url,
            importDataAction.payload.data,
            { headers: { 'Content-Type': createDocumentSuccessAction.payload.contentType } },
          );
        } catch (error: any) {
          yield put(
            importDataFailure({
              clientId: importDataAction.payload.clientId,
              provider: importDataAction.payload.provider,
              errorDescription: `${error.name} ${error.message}`,
              ...error,
            }),
          );
          return;
        }

        if (dataUploadResponse && dataUploadResponse.status === 200) {
          yield put(
            documentsActions.getDocument(documentsTypes.mkDocument({ id: createDocumentSuccessAction.payload.id })),
          );
          const { getDocumentSuccessAction, getDocumentFailureAction } = yield race({
            getDocumentSuccessAction: take(
              // @ts-expect-error FIXME TS7006
              (action) =>
                action.type === documentsActions.getDocumentSuccess.toString() &&
                action.payload.resources &&
                action.payload.resources.length === 1 &&
                action.payload.resources[0].id === createDocumentSuccessAction.payload.id,
            ),
            getDocumentFailureAction: take(
              // @ts-expect-error FIXME TS7006
              (action) =>
                action.type === documentsActions.getDocumentFailure.toString() &&
                action.payload.resources &&
                action.payload.resources.length === 1 &&
                action.payload.resources[0].id === createDocumentSuccessAction.payload.id,
            ),
          });

          const documentId =
            getDocumentSuccessAction &&
            getDocumentSuccessAction.payload.resources &&
            getDocumentSuccessAction.payload.resources.length === 1 &&
            getDocumentSuccessAction.payload.resources[0].id;
          if (documentId) {
            yield put(
              pfmImport({
                clientId: importDataAction.payload.clientId,
                accountId: importDataAction.payload.accountId,
                provider: importDataAction.payload.provider,
                documentId,
              }),
            );
            const { pfmImportSuccessAction, pfmImportFailureAction } = yield race({
              pfmImportSuccessAction: take(
                // @ts-expect-error FIXME TS7006
                (action) =>
                  action.type === pfmImportSuccess.toString() &&
                  action.payload.clientId === importDataAction.payload.clientId,
              ),
              pfmImportFailureAction: take(
                // @ts-expect-error FIXME TS7006
                (action) =>
                  action.type === pfmImportFailure.toString() &&
                  action.payload.clientId === importDataAction.payload.clientId,
              ),
            });

            yield put(
              documentsActions.deleteDocument(
                documentsTypes.mkDocument({ id: createDocumentSuccessAction.payload.id }),
              ),
            );

            if (pfmImportSuccessAction) {
              yield put(
                importDataSuccess({
                  clientId: importDataAction.payload.clientId,
                  provider: importDataAction.payload.provider,
                  ...pfmImportSuccessAction.payload,
                }),
              );

              // dirty hack - delete once websocket impelmented
              // @ts-expect-error FIXME TS7057
              const webSocketEnabled = yield select(featureFlagsSelectors.getFeatureFlag, 'webSocketQcs');
              if (!webSocketEnabled) {
                yield delay(3000);
                yield put(transactionsActions.getTransactions(undefined, { context: 'import' }));
                yield put(tagsActions.getTags(undefined, { context: 'import' }));
                yield put(categoriesActions.getCategories(undefined, { context: 'import' }));
              }
            } else {
              yield put(
                importDataFailure({
                  clientId: importDataAction.payload.clientId,
                  provider: importDataAction.payload.provider,
                  errorDescription: 'pfm importer',
                  ...pfmImportFailureAction.payload,
                }),
              );
            }
          } else {
            yield put(
              importDataFailure({
                clientId: importDataAction.payload.clientId,
                provider: importDataAction.payload.provider,
                errorDescription: 'get document url',
                ...(getDocumentSuccessAction ? getDocumentSuccessAction.payload : getDocumentFailureAction.payload),
              }),
            );
          }
        } else {
          yield put(
            importDataFailure({
              clientId: importDataAction.payload.clientId,
              provider: importDataAction.payload.provider,
              errorDescription: 'document upload',
              ...dataUploadResponse,
            }),
          );
        }
      } else {
        yield put(
          importDataFailure({
            clientId: importDataAction.payload.clientId,
            provider: importDataAction.payload.provider,
            errorDescription: 'create document placeholder',
            ...(createDocumentFailureAction
              ? createDocumentFailureAction.payload
              : createDocumentSuccessAction.payload),
          }),
        );
      }
    },
  );
}

export function* importDataSuccessActionWatcher() {
  yield takeEvery(importDataSuccess, function* _importDataSuccess(importDataSuccessAction: any) {
    tracker.track(tracker.events.csvImport, {
      status: 'complete',
      type: importDataSuccessAction.payload.provider,
    });
  });
}

export function* importDataFailureActionWatcher() {
  yield takeEvery(importDataFailure, function* _importDataFailure(importDataFailureAction: any) {
    tracker.track(tracker.events.csvImport, {
      status: 'error',
      type: importDataFailureAction.payload.provider,
      error_description: importDataFailureAction.payload.errorDescription,
    });
  });
}

export function* pfmImportNudgeUpdater() {
  yield put(transactionsActions.getTransactions(undefined, { context: 'import' }));
  yield put(tagsActions.getTags(undefined, { context: 'import' }));
  yield put(categoriesActions.getCategories(undefined, { context: 'import' }));
}

export function* pfmImportJobUpdater(data: BumpResponseType) {
  if (data.isProcessing === false) {
    const type = data.destinationUrl === '/pfm-importer/import-balances' ? 'Balances' : 'MintTransactions';
    if (data.status === 'COMPLETE') {
      tracker.track(tracker.events.csvImport, { status: 'success', type });
    } else if (data.status === 'ERROR') {
      tracker.track(tracker.events.csvImport, { status: 'error', type });
    } else if (data.status === 'TIMEOUT') {
      tracker.track(tracker.events.csvImport, { status: 'timeout', type });
    }
    executeCallback(data);
  }
}

export function* pfmImportMintPromoExtendActionWatcher() {
  yield takeLeading(pfmImportMintPromoExtend, resourceSagaUtils.qcsRequest, {
    method: 'POST',
    path: '/pfm-importer/mint-promo-extend',
    successAction: pfmImportMintPromoExtendSuccess,
    failureAction: pfmImportMintPromoExtendFailure,
  });
}

export default [
  importDataActionWatcher,
  importDataSuccessActionWatcher,
  importDataFailureActionWatcher,
  pfmImportActionWatcher,
  pfmImportParsePostActionWatcher,
  pfmImportHistoricalBalancesActionWatcher,
  pfmImportMultiAccountImportActionWatcher,
  pfmImportMintPromoExtendActionWatcher,
];
